/**
 * WorkflowSimulatorDialog - "Testar processo" (simulação em modo seco)
 *
 * Percorre o desenho do processo pela RPC `simulate_workflow_definition`, que é
 * apenas leitura: nada é gravado e nenhuma ação é executada de verdade. Serve
 * para conferir o caminho que o processo seguiria com um contexto de exemplo.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-30 v1.0.0 - Criação inicial (Fase 8 do Workflow 2.0)
 */

import { useState } from 'react';
import { FlaskConical, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useWorkflowSimulation } from '@/hooks/useWorkflowDesigner';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

const logger = createLogger('WorkflowSimulatorDialog');

interface Props {
  definitionId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function WorkflowSimulatorDialog({ definitionId, open, onOpenChange }: Props) {
  // Contexto de teste em JSON (limitado para evitar payloads abusivos)
  const [contexto, setContexto] = useState('{\n  "valor": 1000\n}');
  const simulacao = useWorkflowSimulation(definitionId);

  const executar = async () => {
    let parsed: Record<string, unknown> = {};

    try {
      parsed = contexto.trim() ? JSON.parse(contexto) : {};
      if (typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('O contexto precisa ser um objeto JSON.');
      }
    } catch (error: any) {
      toast.error('Contexto inválido: informe um JSON de objeto.');
      logger.warn('Contexto de simulação inválido', { error: error?.message });
      return;
    }

    await simulacao.mutateAsync(parsed).catch(() => undefined);
  };

  const passos = simulacao.data?.passos ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85dvh] max-w-2xl overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FlaskConical className="h-4 w-4" /> Testar processo
          </DialogTitle>
          <DialogDescription>
            A simulação é apenas um ensaio: nada é gravado e nenhuma ação é executada.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-2">
            <Label>Contexto de teste (JSON)</Label>
            <Textarea
              rows={4}
              value={contexto}
              onChange={(e) => setContexto(e.target.value.slice(0, 4000))}
              maxLength={4000}
              className="font-mono text-xs"
            />
          </div>

          <Button size="sm" onClick={executar} disabled={simulacao.isPending || !definitionId}>
            {simulacao.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Rodar simulação
          </Button>

          {simulacao.data && (
            <ScrollArea className="h-[45dvh] rounded-lg border">
              <ol className="divide-y">
                {passos.map((passo, i) => (
                  <li key={`${passo.node_id}-${i}`} className="space-y-1 p-3">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">{passo.ordem}</Badge>
                      <span className="text-sm font-medium text-foreground">{passo.label}</span>
                      <span className="text-xs text-muted-foreground">{passo.node_type}</span>
                    </div>

                    {Array.isArray(passo.acoes) && passo.acoes.length > 0 && (
                      <p className="text-xs text-muted-foreground">
                        Ações: {passo.acoes.map((a) => a.action_code).join(', ')}
                      </p>
                    )}

                    {passo.observacao && (
                      <p className="text-xs text-muted-foreground">{passo.observacao}</p>
                    )}
                  </li>
                ))}

                {passos.length === 0 && (
                  <li className="p-4 text-sm text-muted-foreground">
                    Nenhum caminho percorrido com esse contexto.
                  </li>
                )}
              </ol>
            </ScrollArea>
          )}

          {simulacao.data?.limite_atingido && (
            <p className="text-xs text-warning">
              Limite de passos atingido: o desenho pode conter um laço muito longo.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
